import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Shield, DollarSign } from "lucide-react";
import { format } from "date-fns";

export default function InstitutionalSponsors() {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    sponsor_name: "",
    category: "",
    exclusivity_terms: "",
    start_date: "",
    end_date: "",
    contract_value: "",
    restrictions: "",
    contact_name: "",
    contact_email: "",
    active: true,
    notes: "",
  });

  const { data: sponsors, isLoading } = useQuery({
    queryKey: ["institutional_sponsors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("institutional_sponsors" as any)
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const addSponsorMutation = useMutation({
    mutationFn: async (sponsor: typeof formData) => {
      const { error } = await supabase
        .from("institutional_sponsors" as any)
        .insert([{
          ...sponsor,
          contract_value: sponsor.contract_value ? parseFloat(sponsor.contract_value) : null,
        }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["institutional_sponsors"] });
      toast.success("Sponsor added successfully");
      setIsAddDialogOpen(false);
      setFormData({
        sponsor_name: "",
        category: "",
        exclusivity_terms: "",
        start_date: "",
        end_date: "",
        contract_value: "",
        restrictions: "",
        contact_name: "",
        contact_email: "",
        active: true,
        notes: "",
      });
    },
    onError: (error) => {
      toast.error("Failed to add sponsor: " + error.message);
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const isExpired = (endDate: string | null) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Institutional Sponsors</h1>
            <p className="text-muted-foreground">
              Manage exclusive institutional partnerships and sponsorships
            </p>
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Sponsor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Institutional Sponsor</DialogTitle>
                <DialogDescription>
                  Add a new institutional sponsorship agreement
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sponsor_name">Sponsor Name *</Label>
                    <Input
                      id="sponsor_name"
                      value={formData.sponsor_name}
                      onChange={(e) => setFormData({ ...formData, sponsor_name: e.target.value })}
                      placeholder="e.g., Nike, Coca-Cola"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Input
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="e.g., Apparel, Beverage"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="exclusivity_terms">Exclusivity Terms</Label>
                  <Textarea
                    id="exclusivity_terms"
                    value={formData.exclusivity_terms}
                    onChange={(e) => setFormData({ ...formData, exclusivity_terms: e.target.value })}
                    placeholder="Describe the exclusivity agreement"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract_value">Contract Value</Label>
                  <Input
                    id="contract_value"
                    type="number"
                    value={formData.contract_value}
                    onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="restrictions">Restrictions</Label>
                  <Textarea
                    id="restrictions"
                    value={formData.restrictions}
                    onChange={(e) => setFormData({ ...formData, restrictions: e.target.value })}
                    placeholder="Any restrictions imposed by this sponsorship"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  />
                  <Label htmlFor="active" className="cursor-pointer">
                    Active sponsorship
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Internal Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Internal notes"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => addSponsorMutation.mutate(formData)}
                  disabled={!formData.sponsor_name || !formData.category || !formData.start_date || addSponsorMutation.isPending}
                >
                  Add Sponsor
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <div className="grid gap-4">
            {sponsors?.map((sponsor: any) => (
              <Card key={sponsor.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      <CardTitle>{sponsor.sponsor_name}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{sponsor.category}</Badge>
                      {sponsor.active && !isExpired(sponsor.end_date) ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                      {sponsor.exclusivity_terms && (
                        <Badge variant="destructive">Exclusive</Badge>
                      )}
                    </div>
                  </div>
                  {sponsor.exclusivity_terms && (
                    <CardDescription>{sponsor.exclusivity_terms}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Period:</span>{" "}
                      {format(new Date(sponsor.start_date), "MMM d, yyyy")}
                      {sponsor.end_date && ` - ${format(new Date(sponsor.end_date), "MMM d, yyyy")}`}
                    </div>
                    {sponsor.contract_value && (
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        <span className="font-medium">Value:</span> {formatCurrency(sponsor.contract_value)}
                      </div>
                    )}
                    {sponsor.restrictions && (
                      <div className="col-span-2">
                        <span className="font-medium">Restrictions:</span>
                        <p className="text-muted-foreground mt-1">{sponsor.restrictions}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {sponsors?.length === 0 && (
              <Card>
                <CardContent className="text-center py-8 text-muted-foreground">
                  No institutional sponsors found. Add one to get started.
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
